import { cva, type VariantProps } from 'class-variance-authority';

import { cn, Flex, Icon, Item, ItemContent, Text } from '@kognitos/lattice';

import { EBrowserStepStatus, EStepType } from '../types';
import { StepTypeIndicator } from './StepTypeIndicator';

const browserStepVariants = cva(
  'w-full rounded-lg border overflow-hidden shadow-sm transition-colors',
  {
    variants: {
      status: {
        [EBrowserStepStatus.COMPLETE]:
          'border-border bg-transparent hover:bg-card/20',
        [EBrowserStepStatus.CURRENT]: 'border-ring bg-card',
        [EBrowserStepStatus.PLANNED]: 'border-border opacity-50',
      },
    },
    defaultVariants: {
      status: EBrowserStepStatus.COMPLETE,
    },
  },
);

/**
 * Props for the BrowserStep component.
 */
export interface IBrowserStepProps
  extends VariantProps<typeof browserStepVariants> {
  /** The title/description of the step. */
  title: string;
  /** The status of the step. */
  status?: EBrowserStepStatus;
  /** The type of step (for the icon indicator). */
  stepType?: EStepType;
  /** Time to display. Hidden while the step's loader is showing. */
  time?: string;
  /** Whether to show the time. */
  showTime?: boolean;
  /** Whether to show a loading indicator (only for current steps). */
  showLoader?: boolean;
  /** Additional CSS classes. */
  className?: string;
  /** Click handler for the step. */
  onClick?: () => void;
}

/**
 * BrowserStep displays a single step in the browser automation process.
 *
 * @param props - Component props
 * @returns BrowserStep component
 *
 * @example
 * <BrowserStep
 *   title="Open Google Flights"
 *   status={EBrowserStepStatus.COMPLETE}
 *   stepType={EStepType.NAVIGATE}
 *   time="0:39"
 * />
 *
 * <BrowserStep
 *   title="Locate search button"
 *   status={EBrowserStepStatus.CURRENT}
 *   stepType={EStepType.GOAL}
 *   showLoader
 * />
 */
export function BrowserStep({
  title,
  status = EBrowserStepStatus.COMPLETE,
  stepType = EStepType.GOAL,
  time,
  showTime = true,
  showLoader = false,
  className,
  onClick,
}: IBrowserStepProps) {
  const isCurrent = status === EBrowserStepStatus.CURRENT;

  return (
    <Item
      size="sm"
      className={cn(
        browserStepVariants({ status }),
        onClick && 'cursor-pointer',
        className,
      )}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      {/* Step type indicator - shown on the left for all steps */}
      <StepTypeIndicator type={stepType} isCurrent={isCurrent} />

      {/* Title text */}
      <ItemContent>
        <Text
          level="small"
          color={isCurrent ? 'default' : 'muted'}
          className="min-w-0"
        >
          {title}
        </Text>
      </ItemContent>

      {showTime && time && !(isCurrent && showLoader) && (
        <Text level="small" className="text-muted-foreground/50 shrink-0">
          {time}
        </Text>
      )}

      {/* Loading indicator for current steps */}
      {isCurrent && showLoader && (
        <Flex align="center" className="p-0.5">
          <Icon type="LoaderCircle" size="sm" className="animate-spin" />
        </Flex>
      )}
    </Item>
  );
}

export { browserStepVariants };
